import React, { Suspense, lazy } from 'react';
import { Spinner, Center, Box } from '@chakra-ui/react';
import FusionLoading from './FusionLoading';

// Fallback component for loading states
const LoadingFallback: React.FC<{ height?: string }> = ({
	height = '200px',
}) => (
	<Center h={height}>
		<FusionLoading />
	</Center>
);

// Lazy load heavy components
export const LazyDataTable = lazy(() =>
	import('./DataTable/DataTable').then((module) => ({
		default: module.default,
	})),
);

export const LazyGatherInfoForm = lazy(() =>
	import('../campaigns/GatherInfoForm').then((module) => ({
		default: module.default,
	})),
);

export const LazyProductFormModal = lazy(() =>
	import('../commerce/products/ProductFormModal').then((module) => ({
		default: module.default,
	})),
);

// Higher-order component for wrapping lazy components
export const withLazyLoading = <P extends object>(
	LazyComponent: React.ComponentType<P>,
	fallbackHeight?: string,
) => {
	return React.forwardRef<any, P>((props, ref) => {
		const componentProps = {
			...props,
			ref,
		} as P;

		return (
			<Suspense fallback={<LoadingFallback height={fallbackHeight} />}>
				<LazyComponent {...componentProps} />
			</Suspense>
		);
	});
};

// Optimized wrapper components
export const DataTable = withLazyLoading(LazyDataTable, '400px');
export const GatherInfoForm = withLazyLoading(LazyGatherInfoForm, '600px');
export const ProductFormModal = withLazyLoading(LazyProductFormModal, '400px');

// Intersection Observer based lazy loading for images
interface LazyImageProps {
	src: string;
	alt: string;
	fallbackSrc?: string;
	width?: string | number;
	height?: string | number;
	className?: string;
	onLoad?: () => void;
}

export const LazyImage: React.FC<LazyImageProps> = ({
	src,
	alt,
	fallbackSrc = '/fusion-loader.gif',
	width,
	height,
	className,
	onLoad,
}) => {
	const [imageSrc, setImageSrc] = React.useState<string>(fallbackSrc);
	const [isLoaded, setIsLoaded] = React.useState(false);
	const [isInView, setIsInView] = React.useState(false);
	const imgRef = React.useRef<HTMLImageElement>(null);

	// Intersection Observer for lazy loading
	React.useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsInView(true);
					observer.disconnect();
				}
			},
			{ threshold: 0.1 },
		);

		if (imgRef.current) {
			observer.observe(imgRef.current);
		}

		return () => observer.disconnect();
	}, []);

	// Load actual image when in view
	React.useEffect(() => {
		if (isInView && !isLoaded) {
			const img = new Image();
			img.onload = () => {
				setImageSrc(src);
				setIsLoaded(true);
				onLoad?.();
			};
			img.onerror = () => {
				setImageSrc(fallbackSrc);
				setIsLoaded(true);
			};
			img.src = src;
		}
	}, [isInView, isLoaded, src, fallbackSrc, onLoad]);

	return (
		<Box
			ref={imgRef}
			width={width}
			height={height}
			className={className}
			position="relative"
			overflow="hidden"
		>
			<img
				src={imageSrc}
				alt={alt}
				style={{
					width: '100%',
					height: '100%',
					objectFit: 'cover',
					transition: 'opacity 0.3s ease',
					opacity: isLoaded ? 1 : 0.7,
				}}
			/>
			{!isLoaded && (
				<Center
					position="absolute"
					top={0}
					left={0}
					right={0}
					bottom={0}
					bg="gray.100"
				>
					<Spinner size="sm" />
				</Center>
			)}
		</Box>
	);
};

export default {
	DataTable,
	GatherInfoForm,
	ProductFormModal,
	LazyImage,
	withLazyLoading,
};
